// Styled components and utility UI elements for CaseDetails
// This file defines layout, highlight, and utility components for the CaseDetails view using Stitches.

import { styled, CSS } from "@stitches/react";
import { AttentionIcon, InfoIcon } from "@/components/Icons";

/**
 * highlightVariations
 * Style variant for highlighting fields with differences.
 */
export const highlightVariations = {
  true: {
    "&::before": {
      content: "",
      backgroundColor: "#F2DB1D",
      position: "absolute",
      inset: "-2px -2px -2px -5px",
      opacity: "0.7",
      zIndex: "1000",
      pointerEvents: "none",
    },
  },
};

/**
 * OrderDetailsContainer
 * Main container for the case details view.
 */
export const OrderDetailsContainer = styled("div", {
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  gap: "16px",
});

/**
 * Section
 * Section container with optional border for grouping related content.
 */
export const Section = styled("section", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  gap: "16px",
  padding: "0 10px",

  variants: {
    border: {
      true: {
        "&::after": {
          content: "",
          height: "1px",
          width: "100%",
          backgroundColor: "#B6B7B7",
          display: "block",
          marginTop: "16px",
        },
      },
    },
  },
});

/**
 * Row
 * Flex row for layout, with variants for alignment and spacing.
 */
export const Row = styled("div", {
  display: "flex",
  gap: "24px",
  flexWrap: "wrap",
  variants: {
    reverse: {
      true: {
        justifyContent: "flex-end",
      },
    },
    spaceBetween: {
      true: {
        justifyContent: "space-between",
      },
    },
  },

  h2: {
    fontSize: "24px !important",
  },

  h3: {
    fontSize: "20px !important",
  },

  "h2,h3": {
    fontWeight: "bold !important",
    margin: "0",
  },
});

/**
 * ProcedureNameWrapper
 * Wrapper for procedure name and scheduled name.
 */
export const ProcedureNameWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",

  h4: {
    margin: "0",
  },
});

/**
 * LabeledTextWrapper, LabeledTextHeader, LabeledTextContent, LabeledContentWrapper
 * Utility components for labeled fields with optional highlighting and icons.
 */
export const LabeledTextWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "stretch",
  gap: "4px",
  minWidth: "150px",
});

export const LabeledTextHeader = styled("span", {
  color: "#5D5E5E !important",
});

export const LabeledTextContent = styled("div", {
  margin: "0",
  color: "#000 !important",
  position: "relative",
  variants: {
    highlight: highlightVariations,
  },

  button: {
    padding: "4px 8px !important",
  },
});

export const LabeledContentWrapper = styled("div", {
  position: "relative",
  zIndex: "1001",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

export const AddressContentWrapper = styled(LabeledContentWrapper);

/**
 * AddressWrapper, AddressText, AddressLineTwo
 * Utility components for displaying addresses with optional highlighting.
 */
export const AddressWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const ColorWrapper = styled("div", {
  backgroundColor: "#F5F5F5",
  padding: "16px",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  borderRadius: "4px",
});

export const AddressText = styled("span", {
  position: "relative",
  variants: {
    highlight: highlightVariations,
  },
});

export const AddressLineTwo = styled("div", {
  display: "flex",
  gap: "8px",
});

/**
 * TextItalic
 * Italicized text for timestamps and notes.
 */
export const TextItalic = styled("i", {
  fontStyle: "italic !important",
});

/**
 * UnnamedWrapper
 * Utility wrapper for unnamed sections.
 */
export const UnnamedWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  h2: {
    color: "#B6B7B7 !important",
  },
});

/**
 * InsuranceText, InsuranceWrapper, BottomBorderWrapper
 * Utility components for insurance card display.
 */
export const InsuranceText = styled("p", {
  margin: "0 !important",
  variants: {
    italic: {
      true: {
        fontStyle: "italic !important",
      },
    },
    header: {
      true: {
        fontSize: "16px",
        fontWeight: "bold !important",
      },
    },
  },
});

export const InsuranceWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
});

export const BottomBorderWrapper = styled("div", {
  borderBottom: "1px solid #B6B7B7",
  paddingBottom: "10px",
});

/**
 * LabeledComponent
 * Utility component for rendering a label and value, with optional highlight and icon.
 */
export const LabeledComponent = ({
  label,
  component,
  highlight,
  containerStyles,
  horizontal = false,
  icon,
}: {
  label: string;
  component: React.ReactNode;
  highlight?: boolean;
  containerStyles?: CSS;
  horizontal?: boolean;
  icon?: React.ReactNode;
}) => {
  return (
    <LabeledTextWrapper
      css={{ ...containerStyles, flexDirection: horizontal ? "row" : "column" }}
    >
      <LabeledTextHeader>{label}</LabeledTextHeader>
      <LabeledTextContent highlight={highlight}>
        <LabeledContentWrapper>
          {icon}
          {component}
        </LabeledContentWrapper>
      </LabeledTextContent>
    </LabeledTextWrapper>
  );
};

/**
 * HighlightContainer
 * Container for warning or highlight messages.
 */
export const HighlightContainer = styled("div", {
  backgroundColor: "#FDF9ED",
  padding: "16px",
  borderRadius: "10px",
  display: "flex",
  justifyItems: "center",
});

/**
 * WarningTemplate
 * Utility component for displaying warning messages with an icon.
 */
export const WarningTemplate = ({
  text,
  padded = true,
}: {
  text: string;
  padded?: boolean;
}) => (
  <Row
    css={{
      gap: "8px",
      alignItems: "center",
      color: "#5f5f5f !important",
      padding: padded ? "16px" : "0px",
      paddingLeft: "0",
      display: "flex",
      justifyItems: "center",
      fontWeight: "bold !important",
    }}
  >
    <AttentionIcon width="24px" height="24px" /> {text}
  </Row>
);

/**
 * InfoTemplate
 * Utility component for displaying informational messages with an icon.
 */
export const InfoTemplate = ({ text }: { text: string }) => (
  <Row
    css={{
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: "8px",
      alignItems: "center",
      color: "#5D5E5E !important",
      padding: "16px",
      paddingLeft: "0",
    }}
  >
    <InfoIcon /> {text}
  </Row>
);
